// Firebase functions for managing report cards

import { collection, addDoc, serverTimestamp, query, where, orderBy, getDocs, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './config';

/**
 * Saves a holistic report card to Firestore.
 * @param {object} reportCardData The report card data from the form.
 * @returns {Promise<string>} The ID of the newly created document.
 */
export const saveHolisticReportCard = async (reportCardData) => {
  if (!auth.currentUser) {
    throw new Error('User is not authenticated.');
  }

  try {
    const docRef = await addDoc(collection(db, 'holisticReportCards'), {
      ...reportCardData,
      createdBy: auth.currentUser.uid,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error saving holistic report card: ', error);
    throw new Error('Failed to save report card.');
  }
};

/**
 * Fetches holistic report cards for a coaching and optionally a batch.
 * @param {string} coachingId The coaching ID.
 * @param {string} batchId Optional batch ID to filter by.
 * @returns {Promise<Array>} Array of holistic report cards.
 */
export const getHolisticReportCards = async (coachingId, batchId = null) => {
  if (!auth.currentUser) {
    throw new Error('User is not authenticated.');
  }

  try {
    let q;
    if (batchId) {
      q = query(
        collection(db, 'holisticReportCards'),
        where('coachingId', '==', coachingId),
        where('batchId', '==', batchId)
      );
    } else {
      q = query(
        collection(db, 'holisticReportCards'),
        where('coachingId', '==', coachingId)
      );
    }
    
    const snapshot = await getDocs(q);
    const reportCards = [];
    
    for (const docSnap of snapshot.docs) {
      const data = { id: docSnap.id, ...docSnap.data(), type: 'holistic' };
      
      // Get student details
      if (data.studentId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', data.studentId));
          if (userDoc.exists()) {
            data.studentData = userDoc.data();
          }
        } catch (err) {
          console.warn('Student not found for holistic report card:', data.studentId);
        }
      }
      
      reportCards.push(data);
    }
    
    // Sort by createdAt descending
    reportCards.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return dateB - dateA;
    });
    
    return reportCards;
  } catch (error) {
    console.error('Error fetching holistic report cards: ', error);
    throw new Error('Failed to fetch holistic report cards.');
  }
};

/**
 * Updates a holistic report card.
 * @param {string} reportCardId The report card ID to update.
 * @param {object} reportCardData The updated report card data.
 * @returns {Promise<void>}
 */
export const updateHolisticReportCard = async (reportCardId, reportCardData) => {
  if (!auth.currentUser) {
    throw new Error('User is not authenticated.');
  }

  try {
    await updateDoc(doc(db, 'holisticReportCards', reportCardId), {
      ...reportCardData,
      updatedBy: auth.currentUser.uid,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating holistic report card: ', error);
    throw new Error('Failed to update report card.');
  }
};

/**
 * Deletes a holistic report card.
 * @param {string} reportCardId The report card ID to delete.
 * @returns {Promise<void>}
 */
export const deleteHolisticReportCard = async (reportCardId) => {
  if (!auth.currentUser) {
    throw new Error('User is not authenticated.');
  }

  try {
    await deleteDoc(doc(db, 'holisticReportCards', reportCardId));
  } catch (error) {
    console.error('Error deleting holistic report card: ', error);
    throw new Error('Failed to delete report card.');
  }
};
